import { useSnapshot } from 'valtio';
import { serviceState } from '@modules/ServiceModule/workspace/lib/state';
import { ServiceUserUtils } from '@modules/ServiceModule/workspace/lib';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import usePermissions from '@app/permissions/usePermissions';
import { permissions } from '@app/permissions/permissions';
import { UserActivityStatus } from '@api/generated/graphql';

// Hoisted to a stable module-level reference so usePermissions's internal
// useMemo can compare by Object.is and skip recomputing on every render.
const CALL_TRANSFER_TO_ACTIVE_EMPLOYEES_PERMS = [
  permissions.general.ACTION_CALL_OPTIONS.CALL_TRANSFER_TO_ACTIVE_EMPLOYEES
    .VIEW,
];

/**
 * Returns true when the row for `userId` should be greyed out and its radio
 * disabled because the FF is on, the agent lacks the permission, and the
 * target is busy. Reads `serviceState.userById[userId]` directly via
 * useSnapshot so each row subscribes only to its own presence — re-renders
 * stay scoped to the rows whose status actually changed.
 *
 * Fail-closed: if RTCS has not yet synced presence for this row (RTCS still
 * connecting, partial workspace, reconnect in flight), the gate treats the
 * user as busy so a still-loading session does not leak transfers to
 * potentially-active employees.
 */
export const useIsUserBlockedByActivity = (userId: string): boolean => {
  const { userById } = useSnapshot(serviceState);
  const {
    [FeatureFlagKeys.featureTransferOutboundToUserWithActivity]:
      featureTransferOutboundToUserWithActivity,
  } = useFeatureFlags();
  const hasCallTransferToActiveEmployeesPermission = usePermissions(
    CALL_TRANSFER_TO_ACTIVE_EMPLOYEES_PERMS,
    'all',
  );

  // `=== false` (not `!hasPermission`) so that an undefined permission value
  // during auth loading does not satisfy the gate.
  const shouldBlockActiveEmployees =
    !!featureTransferOutboundToUserWithActivity &&
    hasCallTransferToActiveEmployeesPermission === false;

  if (!shouldBlockActiveEmployees) return false;

  const serviceUser = userById[userId];
  if (!serviceUser) return true;
  return (
    ServiceUserUtils.getActivityStatus(serviceUser) !== UserActivityStatus.None
  );
};
